// src/components/issues/MenuActionsFiche.tsx — SCE Issues
// Au téléphone, le menu ⋯ en tête de fiche (20/10) : « Envoyer au BE » et
// « Modifier » y sont rangés avec leur libellé en clair. Trois icônes ne
// tenaient plus sur la ligne des badges (elles passaient à la ligne), et sans
// survol, l'icône « Envoyer » seule ne disait pas « au BE ». La cloche reste
// dehors : elle montre un état (suivi, nombre d'abonnés).
//
// Au bureau, rien ne change : trois icônes avec leur infobulle.

import { useState, type JSX, type ReactNode } from 'react'
import { MoreHorizontal } from 'lucide-react'
import { Popover } from '@/components/ui/Popover'

export interface ActionMenu {
  id: string
  icone: ReactNode
  libelle: string
  detail?: string
  /** Grisée, avec la mention « En cours » (pas encore ouverte). */
  enCours?: boolean
  onChoisir: () => void
}

export function MenuActionsFiche({ actions }: { actions: readonly ActionMenu[] }): JSX.Element {
  const [ancre, setAncre] = useState<HTMLButtonElement | null>(null)
  const [ouvert, setOuvert] = useState(false)
  return (
    <>
      <button
        ref={setAncre}
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label="Autres actions"
        className="flex items-center justify-center flex-shrink-0"
        style={{ width: 44, height: 44, borderRadius: 22, border: 'none', background: 'transparent', color: 'var(--color-black)' }}
      >
        <MoreHorizontal size={20} />
      </button>
      {ouvert && ancre && (
        <Popover ancre={ancre} onClose={() => setOuvert(false)} titre="Actions" largeur={264} alignerADroite>
          <ul role="menu" style={{ listStyle: 'none', margin: 0, padding: 4 }}>
            {actions.map((a) => (
              <li key={a.id} role="none">
                <button
                  type="button"
                  role="menuitem"
                  aria-disabled={a.enCours}
                  onClick={() => {
                    if (a.enCours) return
                    setOuvert(false)
                    a.onChoisir()
                  }}
                  className="flex items-start gap-3 w-full text-left"
                  style={{
                    minHeight: 48,
                    padding: '10px 10px',
                    border: 'none',
                    borderRadius: 8,
                    background: 'transparent',
                    fontFamily: 'inherit',
                    color: a.enCours ? 'var(--color-gray-60)' : 'var(--color-black)',
                    cursor: a.enCours ? 'default' : 'pointer',
                  }}
                >
                  <span style={{ display: 'inline-flex', paddingTop: 2 }}>{a.icone}</span>
                  <span className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
                    <span className="flex items-center gap-2" style={{ fontSize: 15, fontWeight: 500 }}>
                      {a.libelle}
                      {a.enCours && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '0 7px',
                            borderRadius: 999,
                            backgroundColor: 'var(--color-gray-10)',
                            color: 'var(--color-gray-70)',
                          }}
                        >
                          En cours
                        </span>
                      )}
                    </span>
                    {a.detail && <span style={{ fontSize: 12, color: 'var(--color-gray-70)' }}>{a.detail}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Popover>
      )}
    </>
  )
}
